import { prepareHeadersForGlobalFilters } from "../PagesContent/Overview/CommonFunctions";
import { GetData } from "./Functions/SettingData";
import qs from "qs";
import DownloadCSV from "../Helper/CSVOperations/DownloadCSV";

// Spec-defined CSV column order for Platform Alerts download.
// NOTE: Unlike BMS Faults (where /api/diagnostics/v2/list-actions returns rows keyed by
// headerLabel per CLAUDE.md), the /api/alerts/v2/list-actions download response returns rows
// keyed by raw field names as listed in the spec. These keys are NOT headerLabel strings.
export const DOWNLOAD_COLUMN_ORDER = [
    'IMEI', 'licensePlateNo', 'chassisNumber', 'originCountry', 'originState',
    'originCity', 'originDepo', 'vehicleSubType', 'triggeredOn', 'entityAffected',
    'lastUpdatedBy', 'resolvedOn', 'resolutionTime', 'severity',
    'alertName', 'category', 'alertStatus', 'overdueSince', 'entityAffectedDisplayID',
];

const MODULE = 'PlatformAlertsV2';

// ---------------------------------------------------------------------------
// JSDoc typedefs
// ---------------------------------------------------------------------------

/**
 * @typedef {{ start: number, end: number, total: number } | null} PaginationTotal
 */

/**
 * @typedef {{ code: number, msg: string }} ResponseStatus
 */

/**
 * @typedef {{
 *   severityName: string,
 *   total: number,
 *   totalToday: number,
 *   totalBatteriesAffected: number,
 *   overdueCount: number
 * }} SeverityBreakupItem
 */

/**
 * @typedef {{
 *   activeStats: { severityBreakup: SeverityBreakupItem[] },
 *   resolvedStats: { totalBatteriesAffected: number, totalToday: number } | null
 * }} AlertStatsData
 */

/**
 * @typedef {{
 *   batteryID: string,
 *   deviceID: string,
 *   licensePlateNo: string,
 *   vin: string,
 *   batteryStatus: number,
 *   latitude: number | null,
 *   longitude: number | null,
 *   pingStatus: 'Active' | 'Inactive',
 *   severityStats: Array<{ severityName: string, count: number }>
 * }} AlertBattery
 */

/**
 * @typedef {{
 *   id: string,
 *   batteryID: string,
 *   deviceID: string,
 *   alertDescription: string,
 *   alertType: string,
 *   alertStatus: string,
 *   finalStatus: string,
 *   activatedAt: string,
 *   resolvedAt: string | null,
 *   openedAt: string | null,
 *   expiredAt: string | null,
 *   severityName: string,
 *   severityLevel: string,
 *   hierarchy: string,
 *   buildingBlockType: string | null,
 *   isAcknowledged: boolean,
 *   affectedEntity: string,
 *   affectedEntityDisplayID: string,
 *   affectedEntityID: string,
 *   triggerSoc: number | null,
 *   triggerVoltage: number | null,
 *   triggerCurrent: number | null,
 *   triggerTemperature: number | null,
 *   resolutionTime: number | null,
 *   lastUpdatedBy: string | null,
 *   overdueSince: string | null
 * }} AlertIncident
 */

/**
 * @typedef {{
 *   sourceType: 'USER' | 'SYSTEM',
 *   insertionTime: string,
 *   logID: string,
 *   alertMetaID: string,
 *   alertStatus: string,
 *   lastUserUpdatedStatus: string | null,
 *   checklistCompleted: string[],
 *   checklistOpen: string[],
 *   severityLevelID: number | null,
 *   severityName: string | null,
 *   comments: string | null,
 *   updatedByUserID: string | null,
 *   updatedByName: string | null,
 *   finalStatus: string,
 *   customerDBTime: string,
 *   batteryDateTime: string | null,
 *   files: any[]
 * }} ActivityHistoryRow
 */

// ---------------------------------------------------------------------------
// Sort key mapping
// ---------------------------------------------------------------------------

/** Maps UI sort keys to batteries API sort keys */
const BATTERY_SORT_KEY_MAP = {
    batteriesBySeverity: 'severity',
    customerDBTimeInsert: 'addedOn',
};

/** Maps UI sort keys to incidents API sort keys */
const INCIDENT_SORT_KEY_MAP = {
    batteriesBySeverity: 'severity',
    severityLevelID: 'severity',
    bmsAlertStatus: 'alertStatus',
    customerDBTimeInsert: 'activatedAt',
    resolveCustomerDBTime: 'resolvedAt',
};

// Handles both JSON-stringified array ([{...}]) and plain object ({...}) formats
// because GenericDraggableTable's applySort callback sets a plain object
const mapSortBy = (sortByJson, keyMap) => {
    try {
        const parsed = JSON.parse(sortByJson);
        const asArray = Array.isArray(parsed) ? parsed : [parsed];
        const mapped = asArray.map((s) => ({
            ...s,
            sortBy: keyMap[s.sortBy] || s.sortBy,
        }));
        return JSON.stringify(mapped);
    } catch {
        return sortByJson;
    }
};

// ---------------------------------------------------------------------------
// Field normalization — maps real incident field names to what
// AlertListItemIncident expects (shared with BMS Faults).
// Raw incident data is preserved; aliases are added.
// ---------------------------------------------------------------------------

/**
 * @param {AlertIncident} incident
 * @returns {AlertIncident & Record<string, any>}
 */
export const normalizeIncident = (incident) => ({
    ...incident,
    // severity alias: column config may use dataIndex "severity" but API returns "severityName"
    severity: incident.severityName,
    // BMS Faults component aliases
    incidentID: incident.id ?? incident.incidentID ?? '',
    incidentErrorName: incident.alertDescription,
    bmsAlertStatus: incident.finalStatus || incident.alertStatus,
    // editable user status the action dialog's Update Status dropdown initializes from
    // (same key vocabulary the activity POST sends: open/in_progress/action_completed/force_resolved).
    // Absent from the list response -> dialog safely defaults to 'open'.
    incidentErrorStatus: incident.lastUserUpdatedStatus,
    category: incident.alertType,
    customerDBTimeInsert: incident.activatedAt,
    entityAffected: incident.hierarchy,
    entityAffectedDisplayID: incident.affectedEntityDisplayID,
    entityAffectedID: incident.affectedEntityID,
    resolveCustomerDBTime: incident.resolvedAt,
});

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * Stats card — severity breakdown for active (and optionally resolved) alerts.
 * @param {object} globalFilters
 * @returns {Promise<{ response: { data: AlertStatsData }, responseStatus: ResponseStatus }>}
 */
/**
 * Builds and triggers a CSV download with the spec-defined column order.
 * Pass this as the buildCsvFn prop on AlertListHeader for Platform Alerts.
 * @param {object[]} data
 * @param {string} filenamePrefix
 */
export const buildPlatformAlertsCsv = (data, filenamePrefix) => {
    if (!data || data.length === 0) return;
    const ordered = data.map((row) => {
        const out = {};
        DOWNLOAD_COLUMN_ORDER.forEach((key) => { out[key] = row[key] ?? ''; });
        return out;
    });
    DownloadCSV(filenamePrefix, ordered);
};

export const getPlatformAlertStats = async (globalFilters) => {
    const headers = prepareHeadersForGlobalFilters(globalFilters, MODULE);
    const config = {
        method: "get",
        url: `/api/alerts/v2/stats`,
        headers,
        params: { includeResolvedStats: true },
    };
    return GetData(config);
};

/**
 * Batteries list — paginated, filterable, sortable.
 * @param {object} globalFilters
 * @param {object} filters  — pageNum, pageCount, errorStatus, sortBy, + filter keys
 * @returns {Promise<{ response: { data: AlertBattery[], total: PaginationTotal }, responseStatus: ResponseStatus }>}
 */
export const getPlatformAlertBatteriesList = async (globalFilters, filters) => {
    const headers = prepareHeadersForGlobalFilters(globalFilters, MODULE);
    const { sortBy, ...rest } = filters;
    const config = {
        method: "get",
        url: `/api/alerts/v2/batteries`,
        headers,
        params: {
            ...rest,
            ...(sortBy && { sortBy: mapSortBy(sortBy, BATTERY_SORT_KEY_MAP) }),
        },
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
    };
    return GetData(config);
};

/**
 * Incidents for a battery — paginated, filterable, sortable.
 * Returns raw API data. Use normalizeIncident() before passing to AlertListItemIncident.
 * @param {object} globalFilters
 * @param {string} batteryID
 * @param {object} filters  — same filter keys as batteries list
 * @returns {Promise<{ response: { data: AlertIncident[], total: PaginationTotal }, responseStatus: ResponseStatus }>}
 */
export const getPlatformAlertIncidents = async (globalFilters, batteryID, filters) => {
    const headers = prepareHeadersForGlobalFilters(globalFilters, MODULE);
    const { sortBy, ...rest } = filters || {};
    const config = {
        method: "get",
        url: `/api/alerts/v2/incidents/${batteryID}`,
        headers,
        params: {
            ...rest,
            ...(sortBy && { sortBy: mapSortBy(sortBy, INCIDENT_SORT_KEY_MAP) }),
        },
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
    };
    return GetData(config);
};

/**
 * Bulk actions — download CSV or email.
 * Maps AlertListHeader's actionType values to the API's expected values.
 * @param {object} globalFilters
 * @param {object} actionData  — { actionType, resourceIDs, selectAll, emailTo, emailCc, ... }
 * @returns {Promise<{ response: { data: any }, responseStatus: ResponseStatus }>}
 */
export const platformAlertListAction = async (globalFilters, actionData) => {
    const { actionType, resourceIDs = [], selectAll = false, filters, emailTo, emailCc, subject, body: emailBody } = actionData;

    // Map AlertListHeader's action type strings to this API's expected values
    const ACTION_TYPE_MAP = { 'download-csv': 'download', mail: 'email' };
    const mappedActionType = ACTION_TYPE_MAP[actionType] || actionType;

    // Scoping: use explicit selectAll flag (set by AlertListHeader.prepareActionData when user selects all)
    // Never fall back to selectAll based on empty resourceIDs — that would act on the whole fleet
    const scopeFields = selectAll
        ? { selectAll: true, ...(filters && { filters }) }
        : { resourceIDs };

    const headers = prepareHeadersForGlobalFilters(globalFilters, MODULE);
    const body = {
        actionType: mappedActionType,
        ...scopeFields,
        ...(emailTo && { emailTo }),
        ...(emailCc && { emailCc }),
        ...(subject && { subject }),
        ...(emailBody && { body: emailBody }),
    };
    const config = {
        method: "post",
        url: `/api/alerts/v2/list-actions`,
        headers,
        data: body,
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
    };

    return GetData(config);
};

/**
 * Incident detail — full record including escalation path, instructions, etc.
 * @param {string} alertID  — the incident's `id` field
 * @returns {Promise<{ response: { data: object }, responseStatus: ResponseStatus }>}
 */
export const getPlatformAlertIncidentDetail = async (alertID) => {
    const config = {
        method: "get",
        url: `/api/alerts/incident/info/${alertID}`,
    };
    return GetData(config);
};

/**
 * Normalized detail fetcher for SingleAlertAnalysisDialog AND the shared
 * IncidentActionDialog (Smart Alerts). Calls getPlatformAlertIncidentDetail and maps
 * field names to match what those shared components expect (BMS Faults field names):
 *   - escalationPath entries: alertStatus → bmsAlertStatus
 * All original fields (including alertInstructions, the action-dialog checklist source)
 * are spread through untouched — do not drop them when editing this adapter.
 * View Condition rows are built separately by buildPlatformConditionRows from the
 * native alertSeverityMeta — this fetcher no longer reshapes that data.
 * @param {string} alertID
 */
export const getPlatformAlertIncidentDetailForAnalyse = async (alertID) => {
    const res = await getPlatformAlertIncidentDetail(alertID);
    if (!res?.response?.data) return res;

    const data = { ...res.response.data };

    // SingleAlertAnalysisDialog's useEffect guard checks incidentInfo?.incidentID.
    // Platform Alerts response has no incidentID field — inject it so the guard
    // fires without modifying the shared component.
    data.incidentID = alertID;

    if (Array.isArray(data.escalationPath)) {
        data.escalationPath = data.escalationPath.map((e) => ({
            ...e,
            bmsAlertStatus: e.alertStatus,
            severityName: e.severityName || '',
        }));
    }

    // View Condition rows are built by the platform adapter (buildPlatformConditionRows)
    // straight from the native `alertSeverityMeta` shape — no fault-shaped remapping here.

    // The dialog shows getFormattedResolutionTime(incidentInfo.alertDuration) (in
    // minutes) next to the Fault Lifecycle clock. Platform Alerts returns this as
    // alertLifeTime, so alias it.
    if (data.alertDuration == null) {
        data.alertDuration = data.alertLifeTime;
    }

    return { ...res, response: { ...res.response, data } };
};

/**
 * Related signals configured for an alert type (alertMeta.relatedExtendedAttrsMetaIds).
 * Used to seed the default chart signals for Analyse and Advanced Analytics.
 * Each signal: { id, entityType, key, label, unitOfMeasurement, fieldType,
 *   filedDataType, type, signalType, possibleValues }.
 * @param {number|string} alertMetaID
 * @returns {Promise<{ response: { data: object[] }, responseStatus: ResponseStatus }>}
 */
export const getAlertRelatedSignals = async (alertMetaID) => {
    const config = {
        method: "get",
        url: `/api/alerts/v2/meta/${alertMetaID}/related-signals`,
    };
    return GetData(config);
};

/**
 * Convenience wrapper: resolves to the related-signals array (or [] on any
 * error / no-data), so callers can seed defaults without try/catch noise.
 * @param {number|string} alertMetaID
 * @returns {Promise<object[]>}
 */
export const getAlertRelatedSignalsList = async (alertMetaID) => {
    if (alertMetaID == null) return [];
    try {
        const res = await getAlertRelatedSignals(alertMetaID);
        const list = res?.response?.data;
        return Array.isArray(list) ? list : [];
    } catch (e) {
        return [];
    }
};

/**
 * Activity history for an incident.
 * @param {string} alertID
 * @param {object} [params]  — pageNum, pageCount, sourceType, sortOrder, skipPagination
 * @returns {Promise<{ response: { data: ActivityHistoryRow[], total: PaginationTotal }, responseStatus: ResponseStatus }>}
 */
export const getPlatformAlertActivityHistory = async (alertID, params = {}) => {
    const config = {
        method: "get",
        url: `/api/alerts/v2/incidents/${alertID}/activity/history`,
        params: { sortOrder: 'desc', ...params },
        paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "repeat" }),
    };
    return GetData(config);
};

/**
 * Add activity (comment / status update / checklist) to an incident.
 * @param {string} alertID
 * @param {{ status?: string, comments?: string, checklistCompleted?: string[], checklistOpen?: string[] }} logData
 * @returns {Promise<{ response: { data: object }, responseStatus: ResponseStatus }>}
 */
export const addPlatformAlertActivity = async (alertID, logData) => {
    const config = {
        method: "post",
        url: `/api/alerts/v2/incidents/${alertID}/activity`,
        headers: { "Content-Type": "application/json" },
        data: logData,
    };
    return GetData(config);
};

/**
 * Filter dropdown data — fetches dynamic options for filter fields.
 * @param {object} filterConfig  — { queryParams: { table, column, keyColumn? } }
 * @returns {Promise<{ response: { response: { filterData: any[] } }, responseStatus: ResponseStatus }>}
 */
export const getPlatformAlertFilterOptions = async (filterConfig) => {
    const config = {
        method: "get",
        url: `/api/get_filter_data`,
        params: { filterConfig: JSON.stringify(filterConfig) },
    };
    return GetData(config);
};

// ---------------------------------------------------------------------------
// Analytics tab — stubs (implement when backend APIs are ready)
// ---------------------------------------------------------------------------

export const getPlatformAlertAnalyticsKpis = async (globalFilters, filters) => {
    return { response: { data: {} } };
};

export const getPlatformAlertAnalyticsList = async (globalFilters, params) => {
    return null;
};

export const getPlatformAlertAnalyticsListAction = async (globalFilters, params) => {
    return null;
};
